import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {PermissionGroup} from '../../types/permission-group/permissionGroup';
import {RootState} from '../store';
import {PERMISSION_GROUP_CATEGORY} from '../../constants/constants';

interface PermissionGroupState {
  permissionGroups: PermissionGroup[];
  allPermissionGroups: PermissionGroup[];
  refreshPermissionGroups: boolean;
  newlyAddedPermissionGroupId: string;
  /** Client the new group was created for; avoids auto-select on other screens/clients */
  newlyAddedPermissionGroupClientId: string;
  newlyAddedPermissionGroupCategory: PERMISSION_GROUP_CATEGORY | '';
}

const initialState: PermissionGroupState = {
  permissionGroups: [],
  allPermissionGroups: [],
  refreshPermissionGroups: false,
  newlyAddedPermissionGroupId: '',
  newlyAddedPermissionGroupClientId: '',
  newlyAddedPermissionGroupCategory: '',
};

const permissionGroupsSlice = createSlice({
  name: 'permissionGroups',
  initialState,
  reducers: {
    setPermissionGroups(
      state,
      action: PayloadAction<{permissionGroups: PermissionGroup[]}>
    ) {
      state.permissionGroups = action.payload.permissionGroups;
    },
    setAllPermissionGroups(
      state,
      action: PayloadAction<{permissionGroups: PermissionGroup[]}>
    ) {
      state.allPermissionGroups = action.payload.permissionGroups;
    },
    setRefreshPermissionGroups(state, action: PayloadAction<boolean>) {
      state.refreshPermissionGroups = action.payload;
    },
    clearNewlyAddedPermissionGroup(state) {
      state.newlyAddedPermissionGroupId = '';
      state.newlyAddedPermissionGroupClientId = '';
      state.newlyAddedPermissionGroupCategory = '';
    },
    addNewPermissionGroup: (state, action) => {
      const newGroup: PermissionGroup = action.payload;

      const existsInPermissionGroups = state.permissionGroups.some(
        (group) => group.permissionGroupId === newGroup.permissionGroupId
      );
      if (!existsInPermissionGroups) {
        state.permissionGroups = [...state.permissionGroups, newGroup];
      }
      state.permissionGroups.sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );

      const existsInAllPermissionGroups = state.allPermissionGroups.some(
        (group) => group.permissionGroupId === newGroup.permissionGroupId
      );
      if (!existsInAllPermissionGroups) {
        state.allPermissionGroups = [...state.allPermissionGroups, newGroup];
      }
      state.allPermissionGroups.sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );

      state.newlyAddedPermissionGroupId = newGroup.permissionGroupId;
      state.newlyAddedPermissionGroupClientId = newGroup.clientId ?? '';
      state.newlyAddedPermissionGroupCategory = newGroup.category;
      state.refreshPermissionGroups = true;
    },
    setPermissionGroup: (state, action) => {
      const {permissionGroupId, name, description, permissions} =
        action.payload;

      const groupIndex = state.permissionGroups.findIndex(
        (group) => group.permissionGroupId === permissionGroupId
      );
      const groupIndexAllPermission = state.allPermissionGroups.findIndex(
        (group) => group.permissionGroupId === permissionGroupId
      );

      if (groupIndex !== -1) {
        state.permissionGroups[groupIndex].name = name;
        state.permissionGroups[groupIndex].description = description;
        state.permissionGroups[groupIndex].permissions = permissions;
        state.allPermissionGroups[groupIndexAllPermission].name = name;
        state.allPermissionGroups[groupIndexAllPermission].description =
          description;
        state.allPermissionGroups[groupIndexAllPermission].permissions =
          permissions;
      }
    },
    removePermissionGroup: (state, action) => {
      const permissionGroupIdToDelete = action.payload;

      state.permissionGroups = state.permissionGroups.filter(
        (group) => group.permissionGroupId !== permissionGroupIdToDelete
      );
      state.allPermissionGroups = state.allPermissionGroups.filter(
        (group) => group.permissionGroupId !== permissionGroupIdToDelete
      );
    },
    resetPermissionGroups: () => initialState,
  },
  selectors: {
    getPermissionGroups(state) {
      if (state) return state.permissionGroups;
    },
    getAllPermissionGroups(state) {
      if (state) return state.allPermissionGroups;
    },
  },
});

export const {
  setPermissionGroups,
  setAllPermissionGroups,
  resetPermissionGroups,
  setRefreshPermissionGroups,
  clearNewlyAddedPermissionGroup,
  addNewPermissionGroup,
  setPermissionGroup,
  removePermissionGroup,
} = permissionGroupsSlice.actions;
export const {getPermissionGroups, getAllPermissionGroups} =
  permissionGroupsSlice.selectors;

export const permissionGroupSelector = (state: RootState) =>
  state.permissionGroups;

export default permissionGroupsSlice.reducer;
